import { create } from 'zustand';
import {
  DocumentTypeCreateModel,
  DocumentTypeFilter,
  DocumentTypeModel,
  DocumentTypeUpdateModel,
} from '../model/document.type.model';
import axiosInstance from '../../../configs/axios';
import { DocumentTypeChild } from '../data/document.type.child';
import { axiosErrorMessage } from '../../../utils/error-message';
import { AxiosError } from 'axios';
import { DocumentTypeCategoryModel } from '../model/doc-type-cate-model';
import { ApiPath } from '../../../constants/apiPath';
export interface DocumentTypeControllerState {
  listDocTypeCate: DocumentTypeCategoryModel[] | [];
  listDocType: DocumentTypeModel[] | [];
  listCount: number;
  message: string | null;
  loading: boolean;
  filter: DocumentTypeFilter | null;
  page: number;
  pageRowAmount: number;
  activeModal: DocumentTypeChild | null;
  formCreate: DocumentTypeCreateModel | null;
  formUpdate: DocumentTypeUpdateModel | null;
}

export interface DocumentTypeControllerAction {
  docTypeCateGetData: (startLoading?: boolean) => void;
  docTypeGetData: (startLoading?: boolean, filter?: DocumentTypeFilter | null) => void;
  onPageChange: (page: number, rows: number) => void;
  onFilterChange: (filter?: DocumentTypeFilter | null) => void;
  onHandleModal: (activeModal?: DocumentTypeChild | null) => void;
  onUpdateFormCreate: (form: DocumentTypeCreateModel | null) => void;
  onUpdateFormUpdate: (form: DocumentTypeUpdateModel | null) => void;
  onSubmitForm: (form: DocumentTypeCreateModel | null) => void;
  onSubmitFormUpdate: (form: DocumentTypeUpdateModel | null) => void;
  onDelete: (id: string) => void;
}
export const DocumentTypeController = create<
  DocumentTypeControllerState & DocumentTypeControllerAction
>()((set) => ({
  loading: false,
  listDocTypeCate: [],
  listDocType: [],
  message: null,
  listCount: 0,
  page: 0,
  pageRowAmount: 9,
  filter: { skip: 0, take: 9 },
  activeModal: null,
  formCreate: null,
  formUpdate: null,
  docTypeGetData: async (startLoading?: boolean, filter?: DocumentTypeFilter | null) => {
    try {
      if (startLoading) set(() => ({ loading: true }));
      const res = await axiosInstance.get(ApiPath.documentType.all, {
        params: filter ?? { skip: 0, take: 9 },
      });
      const filteredData: any = res?.data?.data ?? [];
      const filteredDataTotal: any = res?.data?.count ?? 0;
      set(() => ({ listDocType: filteredData, listCount: filteredDataTotal }));
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      if (startLoading) setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
  onPageChange: (page: number, rows: number) => {
    set(() => ({ pageRowAmount: rows, page: page }));
  },
  onFilterChange(filter?: DocumentTypeFilter | null) {
    set(() => ({ filter: filter }));
    this.docTypeGetData(true, filter);
  },
  onHandleModal: (activeModal?: DocumentTypeChild | null) => {
    set(() => ({ activeModal: activeModal, message: null }));
    if (activeModal === null) {
      set(() => ({ formCreate: null }));
    }
  },
  onUpdateFormCreate: (form: DocumentTypeCreateModel | null) => {
    set(() => ({ formCreate: form, message: null }));
  },
  onUpdateFormUpdate: (form: DocumentTypeUpdateModel | null) => {
    set(() => ({ formUpdate: form, message: null }));
  },
  async onSubmitForm(form: DocumentTypeCreateModel | null) {
    if (form === null) {
      set(() => ({ message: 'Form is empty!' }));
      return;
    }
    if (!form.flow_type) {
      set(() => ({ formCreate: { ...form }, message: 'flow type required!' }));
    } else if (!form?.document_type_category_id || form?.document_type_category_id?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'document type category required!' }));
    } else if (!form?.name_en || form?.name_en?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'en name required!' }));
    } else if (!form?.name_la || form?.name_la?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'la name required!' }));
    } else if (!form?.description || form?.description?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'destinatio is required!' }));
    } else if (!form?.color_code || form?.color_code?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'color is required!' }));
    } else {
      try {
        set(() => ({ formCreate: form, loading: true }));
        const res = await axiosInstance.post(ApiPath.documentType.create, form);

        if (res?.status === 201) {
          set(() => ({ message: null, formCreate: null, filter: null, page: 0 }));
          this.docTypeGetData(true, null);
          setTimeout(() => this.onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    }
  },
  async onSubmitFormUpdate(form: DocumentTypeUpdateModel | null) {
    if (form === null) {
      set(() => ({ message: 'Form is empty!' }));
      return;
    }
    if (!form.flow_type) {
      set(() => ({ formUpdate: { ...form }, message: 'flow type required!' }));
    } else if (!form?.document_type_category_id || form?.document_type_category_id?.trim() === '') {
      set(() => ({ formUpdate: { ...form }, message: 'document type category required!' }));
    } else if (!form?.name_en || form?.name_en?.trim() === '') {
      set(() => ({ formUpdate: { ...form }, message: 'en name required!' }));
    }
    if (!form?.name_la || form?.name_la?.trim() === '') {
      set(() => ({ formUpdate: { ...form }, message: 'la name required!' }));
    } else {
      try {
        set(() => ({ formUpdate: form, loading: true }));

        const res = await axiosInstance.patch(ApiPath.documentType.update(form.id!), form);
        if (res?.status === 200) {
          set(() => ({ message: null, formUpdate: null, filter: null, page: 0 }));
          this.docTypeGetData(true, null);
          setTimeout(() => this.onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    }
  },
  async onDelete(id: string) {
    try {
      set(() => ({ loading: true }));
      const res = await axiosInstance.delete(ApiPath.documentType.delete(id));
      if (res?.status === 200) {
        this.docTypeGetData(true, null);
      } else {
        set(() => ({ message: res?.statusText }));
      }
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
  docTypeCateGetData: async (startLoading?: boolean) => {
    try {
      if (startLoading) set(() => ({ loading: true }));

      const res = await axiosInstance.get(ApiPath.documentTypeCategory.all);
      const filteredData: any = res?.data?.data ?? [];
      const filteredDataTotal: any = res?.data?.meta?.total ?? 0;
      set(() => ({ listDocTypeCate: filteredData, listCount: filteredDataTotal }));
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      if (startLoading) setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
}));

export default DocumentTypeController;
